import { extractPathFromUrl } from '@/utils/urlUtils';

import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeSecureApolloClient,
} from '@/graphql/client';
import {
  type OrderAppProductVariantsBusinessDetailsQuery,
  type OrderAppProductVariantsBusinessDetailsQueryVariables,
  ProductVariantsBusinessDetailsDocument,
} from '@/graphql/orderApp/api';
import type {
  Image,
  ProductVariantStrapBasicDetailsFragment,
  ProductVariantStrapBasicDetailsQuery,
  ProductVariantStrapBasicDetailsQueryVariables,
  ProductVariantStrapDetailsQuery,
  ProductVariantStrapDetailsQueryVariables,
  ProductVariantWatchBasicDetailsFragment,
  StrapAttributesFragment,
  StrapBuckleSizesQuery,
  StrapBuckleSizesQueryVariables,
  VariantPricingFragment,
} from '@/graphql/saleor/api';
import {
  ProductVariantStrapBasicDetailsDocument,
  ProductVariantStrapDetailsDocument,
  StrapBuckleSizesDocument,
} from '@/graphql/saleor/api';
import {
  checkProductActivation,
  getProduct,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StrapHit } from '@/integrations/algolia/types';
import { fetchDetailPageDataWithExtraData } from '@/integrations/contentful';
import { getMultiselectValue } from '@/lib/attributes';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import {
  DETAIL_PAGE_IMAGE_SIZE,
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  WORLD_WIDE_CATALOG_COUNTRY,
} from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import { serializeStrapPdpMarkdown, withMarkdownExport } from '@/lib/markdown';
import { STRAP_PDP_ERRORS } from '@/lib/notFoundErrors';
import { makeStrapProductUrl, resolveStrapCanonical } from '@/lib/product';
import {
  getContentfulRegionFromLocale,
  getGqlRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import { SEO_HREFLANG_COUNTRIES } from '@/lib/seo/Config';
import { makeActivatedProductHrefLangs } from '@/lib/seo/Functions';
import {
  extractProductStructuredData,
  extractSeoVariablesFromStrapProductVariant,
} from '@/lib/seo/SeoVariables';
import { formatProductSlug } from '@/lib/util';
import type { StrapViewProps } from '@/views/StrapView';
import { areParamsValid, isValidVariant } from '@/views/StrapView/ssr';
import { StrapViewWrapper } from '@/views/StrapView/StrapViewWrapper';

export const getServerSideProps = withMarkdownExport(
  serializeStrapPdpMarkdown,
  commonServerSideProps<
    StrapViewProps,
    {
      lang: string;
      material: string;
      sku: string;
    }
  >(async (context) => {
    const { params, query } = context;
    const draftMode = resolveContentDraftMode(context);
    const { lang: locale, material: paramsMaterial, sku } = params!;

    const region = getRegionFromLocale(locale);
    const regionVariables = getGqlRegionFromLocale(locale);
    const client = initializeSecureApolloClient();

    const { countryCode } = getContentfulRegionFromLocale(locale);
    const contentfulLocale = region.locale.contentful ?? region.locale.locale;

    const [
      { data },
      { cmsData, cmsDefaultLayoutData, cmsExtraData, seoConfigBuilder },
    ] = await Promise.all([
      client.query<
        ProductVariantStrapBasicDetailsQuery,
        ProductVariantStrapBasicDetailsQueryVariables
      >({
        query: ProductVariantStrapBasicDetailsDocument,
        variables: {
          sku: sku.toUpperCase(),
          languageCode: regionVariables.languageCode,
          thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
          imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
          largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
        },
      }),
      await fetchDetailPageDataWithExtraData(
        'strap',
        sku,
        contentfulLocale,
        '',
        '',
        regionVariables.languageCode,
        regionVariables.channel,
        countryCode,
        draftMode,
      ),
    ]);

    const { data: variantDetails } = await client.query<
      ProductVariantStrapDetailsQuery,
      ProductVariantStrapDetailsQueryVariables
    >({
      query: ProductVariantStrapDetailsDocument,
      variables: {
        sku: sku,
        ...regionVariables,
      },
    });

    if (!isValidVariant(data)) {
      capture404Event('Strap PDP', {
        requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
        sku: sku,
        reason: STRAP_PDP_ERRORS.VARIANT_MISSING,
      });
      return {
        notFound: true,
        props: { data: null },
      };
    }

    const dataMaterial =
      data.productVariant!.product.strapMaterial!.values[0]?.name ??
      'Calfskin leather';

    const path = makeStrapProductUrl(dataMaterial, sku, true);

    // Malformed params, redirect to proper path.
    if (!areParamsValid(dataMaterial, paramsMaterial)) {
      const queryString = new URLSearchParams(
        query as Record<string, string>,
      ).toString();
      const destination = `/${locale}${path}${
        queryString ? `?${queryString}` : ''
      }`;

      return {
        redirect: {
          destination,
          permanent: true,
          query,
        },
        props: {
          data: data.productVariant!,
          cmsData,
        },
      };
    }
    const algoliaSsrSearchClient = createSearchClient({
      isPreview: draftMode,
      isSsr: true,
    });

    const strap = await getProduct<StrapHit>(
      locale,
      sku,
      algoliaSsrSearchClient,
    );
    const strapActivation = checkProductActivation(strap, draftMode);
    const { isPdpActivated } = strapActivation;
    if (!strap || !isPdpActivated) {
      return {
        notFound: true,
      };
    }
    const otherSizesSkus = getMultiselectValue(
      variantDetails.productVariant?.product.otherSizesSkus,
    );
    const compatibleBucklesReferences = getMultiselectValue(
      variantDetails.productVariant?.product.compatibleBuckles,
    );

    // otherSizesSkus already provides candidate SKUs / strap sizes
    const { data: businessData } = await client.query<
      OrderAppProductVariantsBusinessDetailsQuery,
      OrderAppProductVariantsBusinessDetailsQueryVariables
    >({
      query: ProductVariantsBusinessDetailsDocument,
      variables: {
        skus: [sku, ...otherSizesSkus, ...compatibleBucklesReferences],
        channel: regionVariables.channel,
      },
      context: CustomContext({
        client: APOLLO_CLIENTS.orderApp,
        disableErrorNotification: true,
      }),
    });

    const { data: buckleSizesData } = await client.query<
      StrapBuckleSizesQuery,
      StrapBuckleSizesQueryVariables
    >({
      query: StrapBuckleSizesDocument,
      variables: {
        languageCode: regionVariables.languageCode,
        limit: 10,
        filter: {
          slugs: compatibleBucklesReferences.map((slug) =>
            formatProductSlug(slug),
          ),
        },
      },
    });

    const productCatalogMode = WORLD_WIDE_CATALOG_COUNTRY?.includes(
      region.country.code,
    );
    const quantityAvailable =
      businessData.productVariants?.find(
        (productVariant) => productVariant.sku === sku,
      )?.quantityAvailable ?? 0;
    const productStructuredData =
      extractProductStructuredData<ProductVariantStrapBasicDetailsFragment>(
        {
          ...data.productVariant,
          ...{
            pricing: variantDetails?.productVariant?.pricing,
          },
          quantityAvailable,
        } as ProductVariantWatchBasicDetailsFragment & {
          pricing: VariantPricingFragment;
          quantityAvailable: number;
        },
        process.env.NEXT_PUBLIC_URL + `${locale}${path}`,
        productCatalogMode,
      );
    setCacheHeader(CacheHeaderType.PDP, context);

    return {
      props: {
        businessData,
        productVariant: variantDetails.productVariant,
        buckleSizesData,
        draftMode: draftMode ?? false,
        data: data.productVariant!,
        cmsData,
        cmsDefaultLayoutData,
        cmsExtraDataRecord: cmsExtraData,
        seoConfig: await (async () => {
          const activatedHreflangs = await makeActivatedProductHrefLangs(
            data.productVariant!.sku!,
            locale,
            SEO_HREFLANG_COUNTRIES,
            [],
            draftMode,
          );

          const canonicalUrl = await resolveStrapCanonical(
            {
              locale,
              dataMaterial,
              sku,
              otherSizesSkus,
              draftMode,
            },
            {
              currentStrapHit: strap,
            },
          );

          return seoConfigBuilder
            ?.mergeSeoVariables({
              ...extractSeoVariablesFromStrapProductVariant(
                (data?.productVariant?.product as StrapAttributesFragment) ||
                  null,
                region.locale.slug,
              ),
              productName:
                (data?.productVariant?.product?.strapName?.values[0].translation
                  ?.plainText as string | undefined) ??
                data?.productVariant?.product.name,
              thumbnail: data?.productVariant?.product?.thumbnail as Image,
            })
            .setCanonical(canonicalUrl)
            .setHreflangs(activatedHreflangs)
            .build();
        })(),
        productStructuredData: productStructuredData || null,
      },
    };
  }),
);

export default StrapViewWrapper;
